import React from "react";
import {
  Box,
  useTheme,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const fieldNameMap = {
  id: "ID",
  ServiceID: "Service ID",
  ServiceId: "Service ID",

  Date: "Date",
  CheckingDate: "Checking Date",
  TimeStart: "Time Start",
  TimeEnd: "Time End",
  TotalDuration: "Total Duration",
  Duration: "Duration",
  ActualDuration: "Actual Duration",
  Deadline: "Deadline",

  Name: "Name",
  Details: "Details",
  EmailThreadID: "Email Thread Link",

  TranslationStatus: "Translation Status",
  InterpretationStatus: "Interpretation Status",
  OtherServiceStatus: "Other Service Status",

  AccountCharge: "Account Charge",
  TotalCost: "Total Cost",
  AdditionalCost: "Additional Cost",
  LinguistPay: "Linguist Pay",
  Profit: "Profit",
  Margin: "Margin",

  TranslationType: "Translation Type",
  InterpretationType: "Interpretation Type",
  OtherServiceType: "Other Service Type",

  Mode: "Mode",
  Address: "Address",
  Country: "Country of Origin",

  MileageRoundtrip: "Mileage Roundtrip",
  MileageCost: "Mileage Cost",
  ParkingFeesTolls: "Parking Fees",
  TravelTimeCost: "Travel Time Cost",
  PreparationTime: "Preparation Time",
  SchedulingTime: "Scheduling Time",
  TotalLinguistPay: "Total Linguist Pay",
  LinguistPayIncluded: "Linguist Pay Included",
  TotalCostIncluded: "Total Cost Included",
  AccountChargeIncluded: "Account Charge Included",

  ActualStart: "Actual Start",
  ActualEnd: "Actual End",

  Paid: "Paid",
  WordCount: "Word Count",
  NumberOfLanguages: "Number Of Languages",

  ContactPersonName: "Contact Person Name",
  ContactPersonNumber: "Contact Person Number",

  ClientRateDiscount: "Client Rate Discount",
  ServiceCategory: "Service Category",
  RateType: "Rate Type",
  NotarizationOrCOTCost: "Notarization / COT Cost",
  DeliveryType: "Delivery Type",
  PaymentType: "Payment Type",
  PaymentFees: "Payment Fees",
  DeliveryDate: "Delivery Date",
  DeliveryCost: "Delivery Cost",

  Deleted: "Deleted",
  Cancelled: "Cancelled",
  Invoiced: "Invoiced",
  CancellationReason: "Cancellation Reason",

  TargetLanguagesIDs: "Target Languages",

  ClientID: "Client",
  LanguageID: "Language",
  LinguistID: "Linguist",
  RequestorID: "Requestor",
  UnitTypeID: "Unit Type",
  CreatedBy: "Created By",
  CheckedBy: "Checked By",
  InvoiceTo: "Invoicer",

  Client: "Client",
  Requestor: "Requestor",
  Linguist: "Linguist",
  CheckedByMember: "Checked By",
  ChangedByMember: "Changed By",
  Source_Language: "Source Language",
  Service_Note: "Service Note",
  InvoiceToMember: "Invoicer",

  // Client fields
  Account: "Account Name",
  ShortName: "Short Name",
  MainAddress: "Main Address",
  OPIMinimumRate: "OPI Minimum Rate",
  Type: "Type",
  Note: "Note",
  "Service Types": "Service Types",
  "Payment Methods": "Payment Methods",
  "Requestor Added": "Requestor Added",
  "Requestor Deleted": "Requestor Deleted",
  "Requestor Name": "Requestor Name",
  "Requestor Email": "Requestor Email",
  "Requestor Phone": "Requestor Phone",
  "Requestor Ext": "Requestor Ext",
  "Requestor IsMain": "Main Requestor",
  "Requestor IsInvoicer": "Invoicer",
  "Contact Person Added": "Contact Person Added",
  "Contact Person Deleted": "Contact Person Deleted",
  "Contact Person Merged": "Contact Person Merged",
  "Contact Person Name": "Contact Person Name",
  "Contact Person Email": "Contact Person Email",
  "Contact Person Phone": "Contact Person Phone",
  "Contact Person Ext": "Contact Person Ext",
  "Group Name": "Group Name",
  "Group Members": "Group Members",
  "Group Primary Invoicer": "Group Primary Invoicer",
  "Group CC": "Group CC",
};

const noiseFields = new Set([
  "id",
  "createdAt",
  "updatedAt",
  "ServiceID",
  "graphEventId",
  "graphICalUId",
  "graphChangeKey",
  "graphLastSyncedAt",
]);

const parseChanges = (changes) => {
  if (!changes) return {};

  if (typeof changes === "string") {
    try {
      return JSON.parse(changes);
    } catch (error) {
      return {};
    }
  }

  if (typeof changes === "object") return changes;
  return {};
};

const valuesEqual = (left, right) => {
  if (left === right) return true;
  if (left == null && right == null) return true;

  if (typeof left === "object" && typeof right === "object" && left && right) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch (error) {
      return false;
    }
  }

  return false;
};

const toComparableNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  const numericCandidate = trimmed.replace(/[$,%\s,]/g, "");
  if (!numericCandidate) return null;

  const parsed = Number(numericCandidate);
  return Number.isFinite(parsed) ? parsed : null;
};

const isZeroishValue = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value === "string" && value.trim().toLowerCase() === "n/a") {
    return true;
  }
  const numeric = toComparableNumber(value);
  return numeric !== null && numeric === 0;
};

const fieldValuesEqual = (field, left, right) => {
  const normalizedField = String(field || "").toLowerCase();

  // Numeric-appropriate fields: rates, costs, prices, pay, charges, profit,
  // margin, and counts/quantities (UnitCount, WordCount, NumberOfLanguages...).
  const isNumericField =
    /rate/i.test(String(field || "")) ||
    normalizedField.includes("rate") ||
    normalizedField.includes("cost") ||
    normalizedField.includes("price") ||
    normalizedField.includes("pay") ||
    normalizedField.includes("charge") ||
    normalizedField.includes("profit") ||
    normalizedField.includes("margin") ||
    normalizedField.includes("count") ||
    normalizedField.includes("quantity") ||
    normalizedField.includes("numberof") ||
    normalizedField.includes("unitcount") ||
    normalizedField.includes("wordcount");

  if (isNumericField) {
    // Zero-value no-ops (N/A → $0.00, null → 0) are not real changes.
    if (isZeroishValue(left) && isZeroishValue(right)) return true;

    // Numeric-equivalent values (330.00 → 330, 0.00 → 0) are formatting-only.
    const leftNumber = toComparableNumber(left);
    const rightNumber = toComparableNumber(right);

    if (leftNumber !== null && rightNumber !== null) {
      return leftNumber === rightNumber;
    }
  }

  return valuesEqual(left, right);
};

const objectToDisplayValue = (value) => {
  if (!value || typeof value !== "object") return value;

  if (typeof value.Name === "string" && value.Name.trim() !== "") {
    return value.Name;
  }

  if (typeof value.name === "string" && value.name.trim() !== "") {
    return value.name;
  }

  if (value.Language && typeof value.Language.Name === "string") {
    return value.Language.Name;
  }

  return JSON.stringify(value);
};

// Email thread links are stored as a JSON array of ids (2026-10-07) — show
// them as a readable list instead of raw JSON. Legacy single-id values and
// the new JSON form normalize to the same text, so the one-time format
// upgrade never shows up as a phantom change.
const formatEmailThreadHistoryValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const ids = parsed
          .map((item) => String(item || "").trim())
          .filter(Boolean);
        return ids.length ? ids.join(", ") : "N/A";
      }
    } catch (error) {
      return value;
    }
  }
  return value;
};

const countryCodeMap = {
  AF: "Afghanistan", AL: "Albania", DZ: "Algeria", AR: "Argentina", AM: "Armenia",
  AU: "Australia", AT: "Austria", AZ: "Azerbaijan", BD: "Bangladesh", BY: "Belarus",
  BE: "Belgium", BO: "Bolivia", BA: "Bosnia and Herzegovina", BR: "Brazil", BG: "Bulgaria",
  KH: "Cambodia", CM: "Cameroon", CA: "Canada", CL: "Chile", CN: "China", CO: "Colombia",
  CR: "Costa Rica", HR: "Croatia", CU: "Cuba", CY: "Cyprus", CZ: "Czech Republic",
  CD: "DR Congo", DK: "Denmark", DO: "Dominican Republic", EC: "Ecuador", EG: "Egypt",
  SV: "El Salvador", EE: "Estonia", ET: "Ethiopia", FI: "Finland", FR: "France", GE: "Georgia",
  DE: "Germany", GH: "Ghana", GR: "Greece", GT: "Guatemala", HT: "Haiti", HN: "Honduras",
  HK: "Hong Kong", HU: "Hungary", IS: "Iceland", IN: "India", ID: "Indonesia", IR: "Iran",
  IQ: "Iraq", IE: "Ireland", PS: "Palestine", IT: "Italy", JM: "Jamaica", JP: "Japan",
  JO: "Jordan", KZ: "Kazakhstan", KE: "Kenya", KW: "Kuwait", KG: "Kyrgyzstan", LA: "Laos",
  LV: "Latvia", LB: "Lebanon", LY: "Libya", LT: "Lithuania", LU: "Luxembourg", MG: "Madagascar",
  MY: "Malaysia", MR: "Mauritania", MA: "Morocco", MM: "Myanmar", MZ: "Mozambique", NA: "Namibia",
  NP: "Nepal", NL: "Netherlands", NZ: "New Zealand", NI: "Nicaragua", NG: "Nigeria", MK: "North Macedonia",
  NO: "Norway", PK: "Pakistan", PA: "Panama", PG: "Papua New Guinea", PY: "Paraguay", PE: "Peru",
  PH: "Philippines", PL: "Poland", PT: "Portugal", PR: "Puerto Rico", QA: "Qatar", RO: "Romania",
  RU: "Russia", RW: "Rwanda", SA: "Saudi Arabia", SN: "Senegal", RS: "Serbia", SG: "Singapore",
  SK: "Slovakia", SI: "Slovenia", ZA: "South Africa", KR: "South Korea", ES: "Spain", LK: "Sri Lanka",
  SD: "Sudan", SE: "Sweden", CH: "Switzerland", SY: "Syria", TW: "Taiwan", TJ: "Tajikistan",
  TZ: "Tanzania", TH: "Thailand", TN: "Tunisia", TR: "Turkey", UG: "Uganda", UA: "Ukraine",
  AE: "United Arab Emirates", GB: "United Kingdom", US: "United States", UY: "Uruguay",
  UZ: "Uzbekistan", VE: "Venezuela", VN: "Vietnam", WS: "Samoa", ME: "Montenegro",
  ZM: "Zambia", ZW: "Zimbabwe", BW: "Botswana", MU: "Mauritius", FJ: "Fiji",
  SB: "Solomon Islands", TO: "Tonga", VU: "Vanuatu", MT: "Malta", MX: "Mexico", AO: "Angola",
  OM: "Oman", BH: "Bahrain", DJ: "Djibouti", KM: "Comoros", SO: "Somalia",
};

const toDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (Array.isArray(value)) {
    if (!value.length) return "N/A";

    const normalized = value
      .map((item) => {
        if (item === null || item === undefined || item === "") return null;
        if (typeof item === "object") return objectToDisplayValue(item);
        return item;
      })
      .filter((item) => item !== null && item !== undefined && item !== "");

    return normalized.length ? normalized.join(", ") : "N/A";
  }

  if (typeof value === "object") return objectToDisplayValue(value);

  // Convert country code to full country name
  if (typeof value === "string" && countryCodeMap[value.toUpperCase()]) {
    return countryCodeMap[value.toUpperCase()];
  }

  return value;
};

const toDisplayField = (field) => {
  if (fieldNameMap[field]) return fieldNameMap[field];

  if (field.startsWith("Requestor.")) {
    return field.replace("Requestor.", "Requestor ");
  }

  return field;
};

const shouldFormatAsDateField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized === "date" ||
    normalized.endsWith("date") ||
    normalized.includes(" date")
  );
};

const shouldFormatAsDurationField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized.includes("duration") ||
    (normalized.includes("time") && !normalized.includes("timestamp"))
  );
};

const shouldFormatAsTimeField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized.includes("timestart") ||
    normalized.includes("timeend") ||
    normalized.includes("actualstart") ||
    normalized.includes("actualend")
  );
};

const shouldFormatAsCurrencyField = (field) => {
  const normalized = String(field || "").trim().toLowerCase();
  if (!normalized) return false;

  if (normalized.includes("ratetype") || normalized === "margin") {
    return false;
  }

  return (
    normalized.includes("rate") ||
    normalized.includes("cost") ||
    normalized.includes("price") ||
    normalized.includes("pay") ||
    normalized.includes("charge") ||
    normalized.includes("profit")
  );
};

const shouldFormatAsPercentField = (field) => {
  const normalized = String(field || "").trim().toLowerCase();
  return normalized === "margin" || normalized.endsWith(" margin");
};

const formatCurrency = (value) => {
  // N/A (null/undefined/empty/"N/A") is treated as 0 so currency changes
  // show a proper diff (e.g. N/A → $45.00 renders as $0.00 → $45.00 [+$45.00]).
  if (isZeroishValue(value)) return "$0.00";
  const numericValue = toComparableNumber(value);
  if (numericValue === null) return value;
  return `$${numericValue.toFixed(2)}`;
};

const formatPercent = (value) => {
  if (isZeroishValue(value)) return "0.00%";
  const numericValue = toComparableNumber(value);
  if (numericValue === null) return value;
  return `${numericValue.toFixed(2)}%`;
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const strValue = String(value);

  // Match HH:MM:SS or H:MM:SS format
  const match = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match) {
    const hours = match[1];
    const minutes = match[2];
    return `${hours}:${minutes}`;
  }

  return value;
};

const formatTime = (value) => {
  if (value === null || value === undefined || value === "") return value;

  // Try parsing as time (HH:MM:SS or HH:MM format)
  const strValue = String(value);
  const timeMatch = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2];
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes} ${period}`;
  }

  return value;
};

// Compute the numeric delta between two values, when both are numeric.
// Returns null for non-numeric fields/values (text, dates, booleans, etc.)
// so those keep the existing display. The delta is derived from the raw
// parsed numbers, so it works for currency, percent, and plain counts.
// The returned object also carries the display unit ("$", "%", or "") so
// the delta text matches the value's unit (e.g. [+$20] / [+20%]).
const computeNumericDelta = (field, from, to) => {
  const normalizedField = String(field || "").toLowerCase();

  // Only numeric-appropriate fields get a delta (money, rates, counts,
  // percentages, durations-as-numbers). Explicitly exclude fields whose
  // values are non-numeric strings that happen to contain digits.
  const isNumericField =
    shouldFormatAsCurrencyField(field) ||
    shouldFormatAsPercentField(field) ||
    normalizedField.includes("count") ||
    normalizedField.includes("quantity") ||
    normalizedField.includes("numberof") ||
    normalizedField.includes("unitcount") ||
    normalizedField.includes("wordcount") ||
    normalizedField.includes("roundtrip") ||
    normalizedField === "rate";

  if (!isNumericField) return null;

  // N/A (null/undefined/empty/"N/A") counts as 0 so a null → number change
  // still shows its diff (Amine: "N/A should be treated as 0").
  const fromNumber = isZeroishValue(from) ? 0 : toComparableNumber(from);
  const toNumber = isZeroishValue(to) ? 0 : toComparableNumber(to);

  if (fromNumber === null || toNumber === null) return null;

  const delta = toNumber - fromNumber;
  // Avoid showing a 0 delta (no actual change).
  if (Math.abs(delta) < 0.005) return null;

  // Determine the unit that matches how the value is displayed.
  let unit = "";
  if (shouldFormatAsCurrencyField(field)) {
    unit = "$";
  } else if (shouldFormatAsPercentField(field)) {
    unit = "%";
  }

  return { delta, unit };
};

// Units that appear BEFORE the number (currency symbols). Everything else
// ("%", "") renders AFTER the number, matching formatCurrency/formatPercent
// so deltas read like the values they annotate: [+$20.00] / [+5.00%].
const PREFIX_UNITS = new Set(["$", "€", "£", "¥"]);

const formatDeltaText = (delta) => {
  const sign = delta.delta >= 0 ? "+" : "-";
  const magnitude = Math.abs(delta.delta).toFixed(2);
  const unit = delta.unit || "";
  return PREFIX_UNITS.has(unit)
    ? `[${sign}${unit}${magnitude}]`
    : `[${sign}${magnitude}${unit}]`;
};

const formatDateValueForDisplay = (field, value) => {  if (value === null || value === undefined || value === "") return value;

  if (shouldFormatAsPercentField(field)) {
    return formatPercent(value);
  }

  if (shouldFormatAsCurrencyField(field)) {
    return formatCurrency(value);
  }

  // Check time fields first (more specific)
  if (shouldFormatAsTimeField(field)) {
    return formatTime(value);
  }

  // Check duration fields
  if (shouldFormatAsDurationField(field)) {
    return formatDuration(value);
  }

  // Check date fields
  if (shouldFormatAsDateField(field)) {
    const parsed = dayjs(value);
    if (!parsed.isValid()) return value;
    return parsed.format("M/D/YYYY");
  }

  return value;
};

const actionLabelMap = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  CHECK: "Checked",
};

const getActionIcon = (actionKey) => {
  switch (actionKey) {
    case "CREATE":
      return <AddIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "UPDATE":
      return <EditIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "DELETE":
      return <DeleteIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "CHECK":
      return <CheckIcon sx={{ color: "white", fontSize: "14px" }} />;
    default:
      return <EditIcon sx={{ color: "white", fontSize: "14px" }} />;
  }
};

const getActionColor = (theme, actionKey) => {
  switch (actionKey) {
    case "CREATE":
      return theme.palette.success.main;
    case "UPDATE":
      return theme.palette.info.main;
    case "DELETE":
      return theme.palette.error.main;
    case "CHECK":
      return theme.palette.secondary.main;
    default:
      return theme.palette.primary.main;
  }
};

const DUPLICATE_WINDOW_MS = 90 * 60 * 1000;

const getEntryPairs = (entry) => {
  if (!Array.isArray(entry?.Content)) return [];

  return entry.Content.map(
    (row) => `${row.field}|${String(row.from)}|${String(row.to)}`,
  ).sort();
};

const isLegacyTargetLanguageCreate = (entry) => {
  if (entry?.Action !== "Created" || !Array.isArray(entry?.Content))
    return false;

  return (
    entry.Content.length === 1 &&
    (entry.Content[0].field === "TargetLanguage" ||
      entry.Content[0].field === "Target Languages")
  );
};

const isNearDuplicateHistoryEntry = (current, existing) => {
  if (!current || !existing) return false;
  if (current.Action !== existing.Action) return false;
  if (current.Member !== existing.Member) return false;
  if (!Array.isArray(current.Content) || !Array.isArray(existing.Content))
    return false;
  if (!current.Content.length || !existing.Content.length) return false;

  const currentTs = new Date(current.TimeStamp).getTime();
  const existingTs = new Date(existing.TimeStamp).getTime();
  if (!Number.isFinite(currentTs) || !Number.isFinite(existingTs)) return false;
  if (Math.abs(currentTs - existingTs) > DUPLICATE_WINDOW_MS) return false;

  const currentPairs = getEntryPairs(current);
  const existingPairs = getEntryPairs(existing);

  if (!currentPairs.length || !existingPairs.length) return false;

  const currentSet = new Set(currentPairs);
  const existingSet = new Set(existingPairs);

  let overlap = 0;
  currentSet.forEach((pair) => {
    if (existingSet.has(pair)) overlap += 1;
  });

  const smallestSize = Math.min(currentSet.size, existingSet.size);
  if (smallestSize === 0) return false;

  const overlapRatio = overlap / smallestSize;

  return overlap >= 3 && overlapRatio >= 0.6;
};

const isNoteField = (field = "") => {
  const normalized = String(field).trim().toLowerCase();
  return normalized === "note" || normalized === "service note";
};

const getHistoryTitle = (entry, entityType = "assignment") => {
  const member = entry?.Member || "N/A";
  const action = (entry?.Action || "Updated").toLowerCase();
  const content = Array.isArray(entry?.Content) ? entry.Content : [];

  const hasOnlyNoteChanges =
    content.length > 0 && content.every((row) => isNoteField(row?.field));

  if (hasOnlyNoteChanges) {
    if (entry?.Action === "Deleted") return `${member} removed a note`;

    const hasExistingNoteContent = content.some((row) => {
      const fromValue = String(row?.from ?? "")
        .trim()
        .toLowerCase();
      return fromValue !== "" && fromValue !== "n/a" && fromValue !== "null";
    });

    if (hasExistingNoteContent || entry?.Action === "Updated") {
      return `${member} edited a note`;
    }

    return `${member} added a note`;
  }

  // Requestor Group changes (Client-level audit entries from group CRUD)
  const hasGroupChanges = content.some((row) =>
    /^Group /.test(String(row?.field || "")),
  );
  if (hasGroupChanges) {
    if (entry?.Action === "Deleted") {
      return `${member} deleted a Requestor Group`;
    }
    if (entry?.Action === "Created") {
      return `${member} created a Requestor Group`;
    }
    return `${member} updated a Requestor Group`;
  }

  return `${member} ${action} the ${entityType}`;
};

const transformData = (auditLogs = []) => {
  const normalized = auditLogs
    .map((item) => {
      const action = actionLabelMap[item.Action] || item.Action || "Updated";
      const changes = parseChanges(item.Changes);

      const content = Object.entries(changes)
        .filter(([field]) => !noiseFields.has(field))
        .map(([field, change]) => {
          const rawFrom =
            field === "EmailThreadID"
              ? formatEmailThreadHistoryValue(change?.from)
              : change?.from;
          const rawTo =
            field === "EmailThreadID"
              ? formatEmailThreadHistoryValue(change?.to)
              : change?.to;

          const from = toDisplayValue(
            formatDateValueForDisplay(field, rawFrom),
          );
          const to = toDisplayValue(
            formatDateValueForDisplay(field, rawTo),
          );

          if (fieldValuesEqual(field, from, to)) return null;

          return {
            field: toDisplayField(field),
            from,
            to,
            // Numeric delta (e.g. [+20] / [-20]) for numeric changes only.
            delta: computeNumericDelta(field, rawFrom, rawTo),
          };
        })
        .filter(Boolean);

      // If changes are empty for update/check actions, mimic old behavior and hide noise cards.
      if (
        (action === "Updated" || action === "Checked") &&
        content.length === 0
      ) {
        return null;
      }

      // Notes should still appear in the timeline as regular change rows (e.g., Note / Service Note fields).

      return {
        id: item.id,
        ActionKey: (item.Action || "UPDATE").toString().toUpperCase(),
        Action: action,
        // No UserId = a server-side change (script / background job /
        // migration) — label it "Server" instead of "N/A" (2026-09-23, per
        // Amine). A set UserId with a missing user record stays "N/A".
        Member:
          item.User?.Name ||
          (item.UserId == null ? "Server" : "N/A"),
        TimeStamp: item.Timestamp,
        Content: content.length > 0 ? content : null,
        NoteTo: null,
      };
    })
    .filter(Boolean);

  const deduped = [];

  normalized.forEach((entry) => {
    if (isLegacyTargetLanguageCreate(entry)) return;

    const duplicateExists = deduped.some((existing) =>
      isNearDuplicateHistoryEntry(entry, existing),
    );

    if (!duplicateExists) {
      deduped.push(entry);
    }
  });

  return deduped;
};

const History = (props) => {
  const { serviceHistories, entityType = "assignment" } = props;

  const theme = useTheme();

  const transformedServiceHistories = transformData(serviceHistories || []);

  const noHistory = (
    <Paper
      elevation={1}
      sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}
    >
      <Typography color="text.secondary">
        No history recorded for this {entityType} yet, history will show once
        changes are made.
      </Typography>
    </Paper>
  );

  return (
    <Box height="100%" width="100%" p=".75rem 0.25rem">
      {transformedServiceHistories.length === 0 ? (
        noHistory
      ) : (
        <Box sx={{ position: "relative", pl: 4 }}>
          <Box
            sx={{
              position: "absolute",
              left: "16px",
              top: 0,
              bottom: 0,
              width: "2px",
              bgcolor: theme.palette.divider,
            }}
          />

          {transformedServiceHistories.map((singleHistory, index) => {
            const changesCount = singleHistory.Content?.length || 0;

            return (
              <Box
                key={singleHistory.id || index}
                sx={{ position: "relative", mb: 2 }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: "-32px",
                    top: "8px",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    bgcolor: getActionColor(theme, singleHistory.ActionKey),
                    border: `3px solid ${theme.palette.background.alt}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    zIndex: 1,
                  }}
                >
                  {getActionIcon(singleHistory.ActionKey)}
                </Box>

                <Accordion
                  elevation={2}
                  sx={{
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            {getHistoryTitle(singleHistory, entityType)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(singleHistory.TimeStamp).fromNow()} •{" "}
                            {dayjs(singleHistory.TimeStamp).format(
                              "MMM DD, YYYY h:mm A",
                            )}
                            {changesCount > 0
                              ? ` • ${changesCount} field${changesCount > 1 ? "s" : ""}`
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Divider sx={{ mb: 2 }} />

                    {changesCount > 0 ? (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Changes:
                        </Typography>

                        {singleHistory.Content.map(
                          ({ field, from, to, delta }) => (
                            <Box
                              key={`${singleHistory.id}-${field}-${String(from)}-${String(to)}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 2,
                                p: 1,
                                borderRadius: "12px",
                                bgcolor: theme.palette.action.hover,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="500"
                                sx={{ minWidth: "150px" }}
                              >
                                {field}:
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {String(from)}
                              </Typography>
                              <Typography variant="body2">→</Typography>
                              <Typography
                                variant="body2"
                                fontWeight="500"
                              >
                                {String(to)}
                              </Typography>
                              {delta !== null &&
                                delta !== undefined && (
                                  <Typography
                                    variant="body2"
                                    fontWeight="600"
                                    sx={{
                                      color:
                                        delta.delta >= 0
                                          ? theme.palette.success.main
                                          : theme.palette.error.main,
                                    }}
                                  >
                                    {formatDeltaText(delta)}
                                  </Typography>
                                )}
                            </Box>
                          ),
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No detailed changes available
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default History;
